import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { fgText, fmtQty, prApi, prErr } from '@/services/prApi';

const fmtDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const Field = ({ label, value, testId }) => (
  <div><p className="text-[10px] uppercase tracking-wider text-stone-500">{label}</p><p className="text-sm font-semibold" data-testid={testId}>{value}</p></div>
);

const ItemRow = ({ it, n, qty }) => (
  <tr className="break-inside-avoid border-b border-stone-200 align-top" data-testid={`pr-print-row-${it.pr_item_id}`}>
    <td className="py-3 pr-2 text-sm text-stone-500">{n}</td>
    <td className="py-3 pr-3"><div className="grid grid-cols-2 gap-1.5">
      {(it.image_urls || []).slice(0, 3).map((u) => <img key={u} src={u} alt="" className="h-24 w-24 rounded border border-stone-300 object-cover" />)}
      {!(it.image_urls || []).length && <span className="col-span-2 text-xs italic text-stone-400">No photo</span>}</div></td>
    <td className="py-3 pr-3"><p className="text-sm font-semibold">{fgText(it) || it.design_name}</p>
      {it.mapped_fg_name && <p className="font-mono text-xs text-stone-600">FG {it.mapped_fg_name}</p>}
      {it.remarks && <p className="mt-0.5 text-xs text-stone-600">{it.remarks}</p>}</td>
    <td className="whitespace-nowrap py-3 text-right text-base font-bold tabular-nums" data-testid={`pr-print-qty-${it.pr_item_id}`}>{fmtQty(qty)} <span className="text-xs font-normal">{it.unit}</span></td>
  </tr>
);

export default function PRPrint() {
  const { prId } = useParams();
  const navigate = useNavigate();
  const [pr, setPr] = useState(null);
  const [mode, setMode] = useState('balance');
  const [err, setErr] = useState('');
  const picked = useRef(false);
  useEffect(() => {
    prApi.get(prId).then((p) => {
      setPr(p);
      if (!picked.current && !(p.total_balance_qty > 0)) setMode('required');
      picked.current = true;
    }).catch((e) => setErr(prErr(e, 'Could not load the requisition')));
  }, [prId]);
  const choose = (v) => { picked.current = true; setMode(v); };
  if (err) {
    return (<div className="p-10 text-center" data-testid="pr-print-error"><p className="mb-3 text-sm text-rose-600">{err}</p>
      <Button variant="outline" onClick={() => navigate('/pr')} data-testid="pr-print-error-back">Back to requisitions</Button></div>);
  }
  if (!pr) return <div className="p-10 text-center text-stone-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const qtyOf = (it) => (mode === 'balance' ? it.balance_qty : it.required_qty);
  const items = pr.items.filter((it) => qtyOf(it) > 0);
  const site = localStorage.getItem('active_site_name') || localStorage.getItem('active_site_id') || '';

  return (
    <div className="min-h-screen bg-stone-100 print:bg-white" data-testid="pr-print-page">
      <style>{'@page { size: A4; margin: 12mm; }'}</style>
      <div className="flex flex-wrap items-center gap-2 border-b bg-white px-4 pb-3 pt-12 sm:pt-3 print:hidden">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/pr/${prId}`)} data-testid="pr-print-back"><ArrowLeft className="h-4 w-4" /></Button>
        <p className="flex-1 text-sm font-semibold">Requisition sheet for suppliers — no rates are printed</p>
        <div className="flex rounded-md border p-0.5" data-testid="pr-print-qty-mode">
          {[['balance', 'Still to order'], ['required', 'Full requirement']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => choose(v)} data-testid={`pr-print-mode-${v}`}
              className={`rounded px-3 py-1 text-xs transition-colors ${mode === v ? 'bg-indigo-600 text-white' : 'text-stone-600 hover:bg-stone-100'}`}>{l}</button>))}
        </div>
        <Button size="sm" onClick={() => window.print()} data-testid="pr-print-btn"><Printer className="mr-1 h-4 w-4" />Print / Save PDF</Button>
      </div>
      <div className="mx-auto my-6 w-full max-w-[210mm] bg-white p-[12mm] shadow-sm print:my-0 print:max-w-none print:p-0 print:shadow-none">
        <div className="flex items-start justify-between border-b-2 border-stone-900 pb-3">
          <div><p className="text-xs uppercase tracking-[0.2em] text-stone-500">{site}</p>
            <h1 className="text-2xl font-black tracking-tight">Purchase Requisition</h1></div>
          <p className="whitespace-nowrap font-mono text-lg font-bold" data-testid="pr-print-number">{pr.pr_number}</p>
        </div>
        <div className="grid grid-cols-3 gap-4 border-b border-stone-200 py-3">
          <Field label="Date" value={fmtDate(pr.pr_date)} testId="pr-print-date" />
          <Field label="Section" value={pr.section_name} testId="pr-print-section" />
          <Field label="Needed by" value={fmtDate(pr.required_by_date)} testId="pr-print-needed-by" />
        </div>
        {pr.remarks && <p className="border-b border-stone-200 py-2 text-sm text-stone-700" data-testid="pr-print-remarks">{pr.remarks}</p>}
        <table className="mt-2 w-full" data-testid="pr-print-table">
          <thead><tr className="border-b border-stone-900 text-left text-[10px] uppercase tracking-wider text-stone-500">
            <th className="w-8 py-2">#</th><th className="w-[56mm] py-2">Photo</th><th className="py-2">Item / design</th>
            <th className="w-[32mm] py-2 text-right">{mode === 'balance' ? 'Qty needed' : 'Qty required'}</th></tr></thead>
          <tbody>{items.map((it, i) => <ItemRow key={it.pr_item_id} it={it} n={i + 1} qty={qtyOf(it)} />)}</tbody>
        </table>
        {!items.length && <p className="py-8 text-center text-sm text-stone-500" data-testid="pr-print-empty">Nothing is still to order on this requisition</p>}
        <div className="mt-8 break-inside-avoid border-t border-stone-300 pt-3 text-xs text-stone-600">
          Please quote your rate and delivery date for each item, mentioning <span className="font-mono font-semibold">{pr.pr_number}</span>.
        </div>
      </div>
    </div>
  );
}
